import LoadingLogo from '@/components/atoms/LoadingLogo';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import useGetNotificationByIdSuperAdmin from '@/hooks/notifications/useGetNotificationByIdSuperAdmin';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React, { PropsWithChildren } from 'react';

type Props = {
  id?: string;
  isOpen: boolean;
  onClose?: () => void;
};

const ItemRow = ({ label, value, children }: PropsWithChildren<{ label: string; value?: string }>) => {
  return (
    <HStack
      w={'full'}
      gap={'40px'}
      paddingY={'14px'}
      borderBottomWidth={'1px'}
      borderBottomColor={'background.separatorBlue'}
    >
      <VStack width={'120px'} alignItems={'flex-start'}>
        <Text variant={'urbanistMedium18'} fontSize={'14px'} fontWeight={500} color={'text.darkGray'}>
          {label}
        </Text>
      </VStack>
      <VStack alignItems={'flex-start'} width={'full'}>
        <Text variant={'urbanistMedium18'} fontSize={'14px'} fontWeight={600} color={'text.darkGray'}>
          {value}
        </Text>
        {children}
      </VStack>
    </HStack>
  );
};

const NotificationOverviewModal = ({ isOpen, onClose, id }: Props) => {
  const { isLoading, data, isSuccess } = useGetNotificationByIdSuperAdmin(id);

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} title={`Notification Overview`}>
      <VStack minHeight={'50vh'} justifyContent={'center'} alignItems={'center'} width={'full'}>
        {isLoading && <LoadingLogo />}
        {!isLoading && isSuccess && data && (
          <VStack width={'full'} alignItems={'flex-start'}>
            <ItemRow label={'Title'} value={data.title} />
            <ItemRow label={'Message'} value={data.message} />
            <ItemRow label={'Created At'} value={new Date(data.createdAt).toLocaleString()} />
            <ItemRow label={'Updated At'} value={new Date(data.updatedAt).toLocaleString()} />
            <ItemRow label={'Is Read'} value={data.isRead ? 'Yes' : 'No'} />
            <ItemRow label={'Channels'} value={data.channels?.map((channel) => channel).join(', ')} />
            <ItemRow label={'Type'} value={data.type} />
            <ItemRow label={'Statuses'}>
              {Object.entries(data.statuses ?? {}).map(([key, value]) => (
                <Text key={key} variant={'urbanistMedium18'} fontSize={'14px'} fontWeight={600} color={'text.darkGray'}>
                  {`${key}: ${value}`}
                </Text>
              ))}
            </ItemRow>
            <ItemRow label={'Action'} value={data.action ?? '/'} />
            <ItemRow label={'Metadata'}>
              {Object.entries(data.metadata ?? {}).map(([key, value]) => (
                <Text key={key} variant={'urbanistMedium18'} fontSize={'14px'} fontWeight={600} color={'text.darkGray'}>
                  {`${key}: ${value}`}
                </Text>
              ))}
            </ItemRow>
            <ItemRow label={'User ID'} value={data.userId} />
            <ItemRow label={'Notification ID'} value={data._id} />
            <ItemRow label={'Action'} value={data.action ?? 'N/A'} />
          </VStack>
        )}
      </VStack>
    </ModalContainer>
  );
};

export default NotificationOverviewModal;
